import React from 'react';
import {Card, Divider, Space, Tag, Typography} from 'antd';
import {
    BugOutlined,
    CheckCircleOutlined,
    EditOutlined,
    EyeOutlined,
    LoadingOutlined,
    MobileOutlined,
    TagOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

const KioskDebugInfo = ({ kioskState, nfcTagId, KIOSK_STATES }) => {
    if (process.env.NODE_ENV !== 'development') {
        return null;
    }

    const getStateInfo = () => {
        switch (kioskState) {
            case KIOSK_STATES.WAITING_FOR_NFC:
                return {
                    icon: <MobileOutlined style={{ color: '#1890ff' }} />,
                    text: 'Czekam na tag NFC...',
                    color: 'processing'
                };
            case KIOSK_STATES.PLAYER_LOADED:
                return {
                    icon: <CheckCircleOutlined style={{ color: 'var(--color-light-green)' }} />,
                    text: 'Gracz załadowany',
                    color: 'success'
                };
            case KIOSK_STATES.ENTERING_STROKES:
                return {
                    icon: <EditOutlined style={{ color: '#faad14' }} />,
                    text: 'Wprowadź liczbę uderzeń',
                    color: 'warning'
                };
            case KIOSK_STATES.SUBMITTING:
                return {
                    icon: <LoadingOutlined style={{ color: '#722ed1' }} spin />,
                    text: 'Zapisywanie...',
                    color: 'processing'
                };
            default:
                return {
                    icon: <BugOutlined style={{ color: '#ff4d4f' }} />,
                    text: 'Nieznany stan',
                    color: 'error'
                };
        }
    };

    const stateInfo = getStateInfo();

    return (
        <Card
            className="debug-panel"
            style={{
                position: 'fixed',
                bottom: '16px',
                left: '16px',
                background: 'var(--white-10)',
                border: '1px solid var(--white-30)',
                borderRadius: 'var(--radius-8)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                zIndex: 9998,
                minWidth: '280px',
                maxWidth: '320px'
            }}
            size="small"
        >
            <div className="stack-v-sm">
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                }}>
                    <BugOutlined style={{
                        fontSize: '16px',
                        color: 'var(--color-light-green)'
                    }} />
                    <Title level={5} style={{
                        color: 'var(--color-white)',
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: '600'
                    }}>
                        Debug Panel
                    </Title>
                </div>

                {/* Stan kiosku */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                }}>
                    {stateInfo.icon}
                    <Text style={{
                        color: 'var(--white-80)',
                        fontSize: '13px',
                        flex: 1
                    }}>
                        {stateInfo.text}
                    </Text>
                    <Tag color={stateInfo.color} size="small">
                        {kioskState}
                    </Tag>
                </div>

                <Divider style={{
                    margin: '8px 0',
                    borderColor: 'var(--white-30)'
                }} />

                {/* Informacje techniczne */}
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <EyeOutlined style={{
                                fontSize: '12px',
                                color: 'var(--white-60)'
                            }} />
                            <Text style={{
                                color: 'var(--white-60)',
                                fontSize: '11px'
                            }}>
                                Focus:
                            </Text>
                        </div>
                        <Tag size="small" color="blue">
                            {document.activeElement?.tagName || 'NONE'}
                        </Tag>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <TagOutlined style={{
                                fontSize: '12px',
                                color: 'var(--white-60)'
                            }} />
                            <Text style={{
                                color: 'var(--white-60)',
                                fontSize: '11px'
                            }}>
                                NFC Length:
                            </Text>
                        </div>
                        <Tag
                            size="small"
                            color={nfcTagId.length > 0 ? 'green' : 'default'}
                        >
                            {nfcTagId.length}
                        </Tag>
                    </div>

                    {nfcTagId && (
                        <div style={{
                            marginTop: '4px',
                            padding: '6px 8px',
                            background: 'var(--white-20)',
                            borderRadius: 'var(--radius-4)',
                            border: '1px solid var(--white-30)'
                        }}>
                            <Text style={{
                                color: 'var(--white-80)',
                                fontSize: '10px',
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                            }}>
                                {nfcTagId}
                            </Text>
                        </div>
                    )}
                </Space>

                {/* Timestamp */}
                <div style={{
                    textAlign: 'center',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid var(--white-20)'
                }}>
                    <Text style={{
                        color: 'var(--white-40)',
                        fontSize: '10px'
                    }}>
                        {new Date().toLocaleTimeString()}
                    </Text>
                </div>
            </div>
        </Card>
    );
};

export default KioskDebugInfo;